<template>
  <v-container class="payment-details" style="max-width: 900px">
    <v-btn text :to="{ name: 'Booking' }" class="mb-4">
      <v-icon left>mdi-arrow-left</v-icon>Back to bookings
    </v-btn>
    <v-card outlined :loading="loading">
      <v-card-title>
        <v-icon left>mdi-shield-lock</v-icon>Saved payment details
      </v-card-title>
      <v-card-subtitle v-if="summary">
        Booking #{{ summary.id }} · {{ summary.business_name }}
      </v-card-subtitle>
      <v-card-text>
        <v-alert v-if="error" type="error" outlined>{{ error }}</v-alert>
        <template v-if="summary">
          <p>{{ summary.client_name }}</p>
          <v-alert type="info" text dense>
            Numbers are hidden by default. Confirm your password to reveal them.
            They stay visible until you hide them or switch away. Payment detail
            access is recorded.
          </v-alert>
          <v-row>
            <v-col cols="12" md="6">
              <h2 class="text-h6 mb-3">Credit card</h2>
              <dl>
                <dt>Cardholder</dt>
                <dd>{{ payment.name_on_card || 'Not saved' }}</dd>
                <dt>Card number</dt>
                <dd class="payment-number">
                  {{ payment.card_number || 'Not saved' }}
                </dd>
                <dt>Expiry</dt>
                <dd>{{ expiry }}</dd>
                <dt>CVC / CVV</dt>
                <dd class="payment-number">{{ cvcDisplay }}</dd>
              </dl>
            </v-col>
            <v-col cols="12" md="6">
              <h2 class="text-h6 mb-3">Direct debit</h2>
              <dl>
                <dt>Account name</dt>
                <dd>{{ payment.account_name || 'Not saved' }}</dd>
                <dt>BSB</dt>
                <dd class="payment-number">
                  {{ payment.bsb_number || 'Not saved' }}
                </dd>
                <dt>Account number</dt>
                <dd class="payment-number">
                  {{ payment.account_number || 'Not saved' }}
                </dd>
              </dl>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
      <v-card-actions v-if="hasNumbers">
        <v-btn v-if="revealed" color="primary" @click="hideDetails">
          <v-icon left>mdi-eye-off</v-icon>Hide numbers
        </v-btn>
        <v-btn v-else color="primary" @click="openPasswordDialog">
          <v-icon left>mdi-eye</v-icon>Reveal numbers
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      :value="passwordDialog"
      max-width="440"
      @input="onPasswordDialogChange"
    >
      <v-card>
        <v-card-title>Confirm your password</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="reveal">
            <v-text-field
              v-model="password"
              label="Your admin password"
              type="password"
              autocomplete="current-password"
              :disabled="revealing"
              autofocus
              outlined
            />
            <v-alert v-if="revealError" type="error" dense outlined>
              {{ revealError }}
            </v-alert>
            <v-btn
              type="submit"
              color="primary"
              :loading="revealing"
              :disabled="!password || revealing"
            >
              Reveal payment details
            </v-btn>
            <v-btn text @click="hideDetails">Cancel</v-btn>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { axios } from '../../../utils'

export default {
  name: 'BookingPaymentDetails',
  data() {
    return {
      summary: null,
      revealed: null,
      loading: false,
      revealing: false,
      error: '',
      revealError: '',
      passwordDialog: false,
      password: '',
      requestVersion: 0,
      loadVersion: 0
    }
  },
  computed: {
    payment() {
      return this.revealed || (this.summary && this.summary.payment) || {}
    },
    expiry() {
      const { expiry_month, expiry_year } = this.payment
      return expiry_month && expiry_year
        ? `${expiry_month}/${expiry_year}`
        : 'Not saved'
    },
    cvcDisplay() {
      if (this.revealed) return this.revealed.cvv || 'Not saved'
      return this.payment.has_cvv ? '•••' : 'Not saved'
    },
    hasNumbers() {
      const payment = this.summary && this.summary.payment
      return (
        payment &&
        (payment.card_number ||
          payment.account_number ||
          payment.bsb_number ||
          payment.has_cvv)
      )
    }
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler() {
        this.loadSummary()
      }
    }
  },
  mounted() {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    window.addEventListener('blur', this.hideDetails)
    window.addEventListener('pagehide', this.hideDetails)
    window.addEventListener('beforeprint', this.hideDetails)
  },
  beforeDestroy() {
    this.loadVersion += 1
    this.hideDetails()
    this.summary = null
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    window.removeEventListener('blur', this.hideDetails)
    window.removeEventListener('pagehide', this.hideDetails)
    window.removeEventListener('beforeprint', this.hideDetails)
  },
  methods: {
    async loadSummary() {
      const version = ++this.loadVersion
      this.hideDetails()
      this.summary = null
      this.error = ''
      this.loading = true
      try {
        const { data } = await axios.get(
          `api/admin/booking-invoices/${this.$route.params.id}/payment-details`
        )
        if (version === this.loadVersion) this.summary = data
      } catch (error) {
        if (version === this.loadVersion) this.error = this.errorMessage(error)
      } finally {
        if (version === this.loadVersion) this.loading = false
      }
    },
    openPasswordDialog() {
      this.hideDetails()
      this.revealError = ''
      this.passwordDialog = true
    },
    onPasswordDialogChange(open) {
      if (!open && !this.revealed) this.hideDetails()
    },
    async reveal() {
      if (!this.password || this.revealing) return
      const version = ++this.requestVersion
      this.revealing = true
      this.revealError = ''
      const password = this.password
      this.password = ''
      try {
        const {
          data
        } = await axios.post(
          `api/admin/booking-invoices/${this.$route.params.id}/payment-details/reveal`,
          { password }
        )
        // Discard responses that arrive after cancellation, navigation, or tab hiding.
        if (version !== this.requestVersion || document.hidden) return
        this.revealed = data.payment
        this.passwordDialog = false
      } catch (error) {
        if (version === this.requestVersion)
          this.revealError = this.errorMessage(error)
      } finally {
        if (version === this.requestVersion) this.revealing = false
      }
    },
    hideDetails() {
      this.requestVersion += 1
      this.revealed = null
      this.password = ''
      this.passwordDialog = false
      this.revealing = false
    },
    onVisibilityChange() {
      if (document.hidden) this.hideDetails()
    },
    errorMessage(error) {
      const status = error.response && error.response.status
      if (status === 401 || status === 419)
        return 'Your session has expired. Please sign in again.'
      if (status === 403)
        return 'Only administrators can view payment details over HTTPS.'
      if (status === 404) return 'This booking could not be found.'
      if (status === 422)
        return 'Your password could not be confirmed. Please try again.'
      if (status === 429)
        return 'Too many attempts. Please wait a minute before trying again.'
      return 'Payment details could not be loaded. Please try again.'
    }
  }
}
</script>

<style scoped>
dt {
  font-weight: 600;
}
dd {
  margin: 4px 0 16px;
  overflow-wrap: anywhere;
}
.payment-number {
  font-family: monospace;
  font-size: 1.1rem;
}
@media print {
  .payment-details {
    display: none !important;
  }
}
</style>
